import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAdmin } from "@/admin/AdminContext";
import { api } from "@/admin/api";
import { CATALOG, isMajorationDay, fmtPrice } from "@/data/devisCatalog";

const todayIso = () => new Date().toISOString().slice(0, 10);

const EMPTY_CLIENT = {
    type: "particulier", company: "", siret: "", name: "", phone: "", email: "",
    address: "", zip: "", city: "",
    billing_same: true, billing: { address: "", zip: "", city: "" },
};

const FIELD = "w-full rounded-2xl border border-black/15 bg-white px-5 py-4 text-base outline-none transition-colors focus:border-brand";

const AdminDevisForm = () => {
    const { admin } = useAdmin();
    const navigate = useNavigate();
    const { id } = useParams(); // présent en mode modification
    const [client, setClient] = useState(EMPTY_CLIENT);
    const [date, setDate] = useState(todayIso());
    const [items, setItems] = useState([]);
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (admin === null) navigate("/admin", { replace: true });
    }, [admin, navigate]);

    useEffect(() => {
        if (!id || !admin) return;
        api.getDevis(id)
            .then((d) => {
                if (d.status !== "brouillon") {
                    toast.error("Ce devis est déjà signé, modification impossible");
                    navigate(`/admin/devis/${id}`, { replace: true });
                    return;
                }
                setClient({ ...EMPTY_CLIENT, ...d.client, billing: { ...EMPTY_CLIENT.billing, ...(d.client?.billing || {}) } });
                setDate(d.intervention_date);
                setItems(d.items);
                setNotes(d.notes || "");
            })
            .catch((e) => setError(e.message));
    }, [id, admin, navigate]);

    const majoration = isMajorationDay(date);
    const subtotal = useMemo(() => items.reduce((s, i) => s + i.qty * i.unit_price, 0), [items]);
    const total = majoration ? subtotal * 1.2 : subtotal;

    const addItem = (entry) => {
        setItems((prev) => {
            const found = prev.find((i) => i.catalogId === entry.id);
            if (found && entry.id !== "custom") {
                return prev.map((i) => (i.catalogId === entry.id ? { ...i, qty: i.qty + 1 } : i));
            }
            return [...prev, { catalogId: entry.id, label: entry.label, detail: entry.detail || "", qty: 1, unit_price: entry.price }];
        });
    };

    const updateItem = (index, patch) => {
        setItems((prev) => prev.map((i, k) => (k === index ? { ...i, ...patch } : i)));
    };

    const removeItem = (index) => setItems((prev) => prev.filter((_, k) => k !== index));

    const submit = async () => {
        setError("");
        if (client.type === "societe" && !client.company.trim()) {
            setError("Le nom de la société est obligatoire");
            return;
        }
        if (!client.name.trim() || !client.phone.trim()) {
            setError("Nom et téléphone du client sont obligatoires");
            return;
        }
        if (items.length === 0) {
            setError("Ajoutez au moins une prestation");
            return;
        }
        setSaving(true);
        const body = {
            client,
            items: items.map(({ label, detail, qty, unit_price }) => ({ label, detail, qty: Number(qty) || 1, unit_price: Number(unit_price) || 0 })),
            intervention_date: date,
            notes,
        };
        try {
            const saved = id ? await api.updateDevis(id, body) : await api.createDevis(body);
            toast.success(id ? "Devis mis à jour" : `Devis ${saved.number} créé`);
            navigate(`/admin/devis/${saved.id}`, { replace: true });
        } catch (e) {
            setError(e.message);
            setSaving(false);
        }
    };

    if (!admin) return null;

    return (
        <div data-testid="admin-devis-form" className="min-h-screen bg-mist pb-32">
            <header className="sticky top-0 z-40 border-b border-black/10 bg-white">
                <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-3">
                    <Link to={id ? `/admin/devis/${id}` : "/admin/dashboard"} data-testid="admin-form-back" className="inline-flex items-center gap-1.5 rounded-full border border-black/15 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:border-brand hover:text-brand">
                        <ArrowLeft className="h-4 w-4" />
                        Retour
                    </Link>
                    <h1 className="font-display text-base font-black uppercase tracking-tight text-night">
                        {id ? "Modifier le devis" : "Nouveau devis"}
                    </h1>
                </div>
            </header>

            <main className="mx-auto max-w-3xl space-y-6 px-5 pt-6">
                {/* CLIENT */}
                <section className="rounded-3xl border border-black/10 bg-white p-5">
                    <h2 className="font-display text-sm font-black uppercase tracking-[0.15em] text-brand">Le client</h2>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            data-testid="form-type-particulier"
                            onClick={() => setClient({ ...client, type: "particulier" })}
                            className={`rounded-2xl border-2 py-3.5 text-sm font-bold transition-[border-color,background-color] ${client.type === "particulier" ? "border-brand bg-brand/5 text-brand" : "border-black/10 bg-white text-slate-500"}`}
                        >
                            Particulier
                        </button>
                        <button
                            type="button"
                            data-testid="form-type-societe"
                            onClick={() => setClient({ ...client, type: "societe" })}
                            className={`rounded-2xl border-2 py-3.5 text-sm font-bold transition-[border-color,background-color] ${client.type === "societe" ? "border-brand bg-brand/5 text-brand" : "border-black/10 bg-white text-slate-500"}`}
                        >
                            Société
                        </button>
                    </div>
                    <div className="mt-4 space-y-3">
                        {client.type === "societe" && (
                            <>
                                <input data-testid="form-client-company" className={FIELD} placeholder="Nom de la société *" value={client.company} onChange={(e) => setClient({ ...client, company: e.target.value })} />
                                <input data-testid="form-client-siret" className={FIELD} inputMode="numeric" placeholder="SIRET (facultatif)" value={client.siret} onChange={(e) => setClient({ ...client, siret: e.target.value })} />
                            </>
                        )}
                        <input data-testid="form-client-name" className={FIELD} placeholder={client.type === "societe" ? "Nom du contact *" : "Nom et prénom *"} value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} />
                        <input data-testid="form-client-phone" className={FIELD} type="tel" placeholder="Téléphone *" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} />
                        <input data-testid="form-client-email" className={FIELD} type="email" placeholder="Email (facultatif)" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} />
                    </div>
                </section>

                {/* ADRESSES */}
                <section className="rounded-3xl border border-black/10 bg-white p-5">
                    <h2 className="font-display text-sm font-black uppercase tracking-[0.15em] text-brand">Adresse d'intervention</h2>
                    <div className="mt-4 space-y-3">
                        <input data-testid="form-client-address" className={FIELD} placeholder="Adresse de la prestation" value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} />
                        <div className="grid grid-cols-3 gap-3">
                            <input data-testid="form-client-zip" className={FIELD} inputMode="numeric" placeholder="CP" value={client.zip} onChange={(e) => setClient({ ...client, zip: e.target.value })} />
                            <input data-testid="form-client-city" className={`${FIELD} col-span-2`} placeholder="Ville" value={client.city} onChange={(e) => setClient({ ...client, city: e.target.value })} />
                        </div>
                    </div>
                    <label data-testid="form-billing-same" className="mt-4 flex cursor-pointer items-center gap-3 rounded-2xl border border-black/10 bg-mist px-4 py-3.5">
                        <input
                            type="checkbox"
                            checked={client.billing_same}
                            onChange={(e) => setClient((c) => ({
                                ...c,
                                billing_same: e.target.checked,
                                billing: e.target.checked ? c.billing : { address: c.address, zip: c.zip, city: c.city },
                            }))}
                            className="h-5 w-5 accent-brand"
                        />
                        <span className="text-sm font-semibold text-night">Adresse de facturation identique à l'adresse d'intervention</span>
                    </label>
                    {!client.billing_same && (
                        <div className="mt-3 space-y-3 rounded-2xl border border-brand/20 bg-brand/5 p-4">
                            <p className="text-xs font-bold uppercase tracking-[0.15em] text-brand">Adresse de facturation</p>
                            <input data-testid="form-billing-address" className={FIELD} placeholder="Adresse de facturation" value={client.billing.address} onChange={(e) => setClient({ ...client, billing: { ...client.billing, address: e.target.value } })} />
                            <div className="grid grid-cols-3 gap-3">
                                <input data-testid="form-billing-zip" className={FIELD} inputMode="numeric" placeholder="CP" value={client.billing.zip} onChange={(e) => setClient({ ...client, billing: { ...client.billing, zip: e.target.value } })} />
                                <input data-testid="form-billing-city" className={`${FIELD} col-span-2`} placeholder="Ville" value={client.billing.city} onChange={(e) => setClient({ ...client, billing: { ...client.billing, city: e.target.value } })} />
                            </div>
                        </div>
                    )}
                </section>

                {/* DATE */}
                <section className="rounded-3xl border border-black/10 bg-white p-5">
                    <h2 className="font-display text-sm font-black uppercase tracking-[0.15em] text-brand">Date d'intervention</h2>
                    <input
                        data-testid="form-intervention-date"
                        type="date"
                        className={`${FIELD} mt-4`}
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                    />
                    {majoration && (
                        <p data-testid="form-majoration-badge" className="mt-3 inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-2 text-sm font-bold text-amber-700">
                            <AlertTriangle className="h-4 w-4" />
                            Samedi, dimanche ou férié : majoration +20 % appliquée
                        </p>
                    )}
                </section>

                {/* PRESTATIONS */}
                <section className="rounded-3xl border border-black/10 bg-white p-5">
                    <h2 className="font-display text-sm font-black uppercase tracking-[0.15em] text-brand">Prestations</h2>
                    <p className="mt-1 text-xs text-slate-500">Touchez une prestation pour l'ajouter (touchez à nouveau pour +1).</p>
                    <div className="mt-4 grid grid-cols-1 gap-2">
                        {CATALOG.map((entry) => {
                            const selected = items.find((i) => i.catalogId === entry.id);
                            return (
                                <button
                                    key={entry.id}
                                    type="button"
                                    data-testid={`form-catalog-${entry.id}`}
                                    onClick={() => addItem(entry)}
                                    className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3.5 text-left transition-[border-color,background-color] ${selected ? "border-brand bg-brand/5" : "border-black/10 bg-white"}`}
                                >
                                    <span className="text-sm font-semibold text-night">{entry.label}</span>
                                    <span className="shrink-0 text-sm font-black text-brand">
                                        {selected ? `×${selected.qty} · ` : ""}{entry.price > 0 ? fmtPrice(entry.price) : "Libre"}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {items.length > 0 && (
                        <ul className="mt-5 space-y-3 border-t border-black/10 pt-5">
                            {items.map((item, i) => (
                                <li key={`${item.catalogId}-${i}`} data-testid={`form-item-${i}`} className="rounded-2xl border border-black/10 bg-mist p-4">
                                    {item.catalogId === "custom" ? (
                                        <input
                                            data-testid={`form-item-label-${i}`}
                                            className={FIELD}
                                            placeholder="Nom de la prestation"
                                            value={item.label === "Prestation personnalisée" ? "" : item.label}
                                            onChange={(e) => updateItem(i, { label: e.target.value || "Prestation personnalisée" })}
                                        />
                                    ) : (
                                        <p className="text-sm font-bold text-night">{item.label}</p>
                                    )}
                                    <div className="mt-3 flex items-center gap-3">
                                        <div className="flex items-center rounded-full border border-black/15 bg-white">
                                            <button type="button" data-testid={`form-item-minus-${i}`} onClick={() => updateItem(i, { qty: Math.max(1, item.qty - 1) })} className="px-4 py-2.5 text-lg font-black text-brand">−</button>
                                            <span data-testid={`form-item-qty-${i}`} className="min-w-8 text-center text-sm font-black">{item.qty}</span>
                                            <button type="button" data-testid={`form-item-plus-${i}`} onClick={() => updateItem(i, { qty: item.qty + 1 })} className="px-4 py-2.5 text-lg font-black text-brand"><Plus className="h-4 w-4" /></button>
                                        </div>
                                        <div className="flex flex-1 items-center gap-1.5 rounded-full border border-black/15 bg-white px-4 py-2.5">
                                            <input
                                                data-testid={`form-item-price-${i}`}
                                                type="number"
                                                min="0"
                                                step="1"
                                                className="w-full bg-transparent text-sm font-bold outline-none"
                                                value={item.unit_price}
                                                onChange={(e) => updateItem(i, { unit_price: e.target.value })}
                                            />
                                            <span className="text-sm font-bold text-slate-500">€ HT</span>
                                        </div>
                                        <button type="button" data-testid={`form-item-remove-${i}`} onClick={() => removeItem(i)} className="rounded-full p-2.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600">
                                            <Trash2 className="h-5 w-5" />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    <textarea
                        data-testid="form-notes"
                        className={`${FIELD} mt-4`}
                        rows={2}
                        placeholder="Notes internes (facultatif)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    />
                </section>

                {error && <p data-testid="form-error" className="rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</p>}

                {/* TOTAL */}
                <section className="rounded-3xl bg-night p-5 text-white">
                    <div className="flex items-center justify-between text-sm text-white/70">
                        <span>Total HT</span>
                        <span data-testid="form-subtotal">{fmtPrice(subtotal)}</span>
                    </div>
                    {majoration && (
                        <div className="mt-2 flex items-center justify-between text-sm text-amber-300">
                            <span>Majoration +20 %</span>
                            <span data-testid="form-surcharge">{fmtPrice(total - subtotal)}</span>
                        </div>
                    )}
                    <div className="mt-3 flex items-center justify-between border-t border-white/15 pt-3">
                        <span className="font-display text-sm font-black uppercase tracking-wide">Total à facturer</span>
                        <span data-testid="form-total" className="font-display text-2xl font-black">{fmtPrice(total)}</span>
                    </div>
                    <p className="mt-1 text-right text-[11px] text-white/50">TVA non applicable, art. 293 B du CGI</p>
                </section>
            </main>

            <div className="fixed inset-x-0 bottom-0 z-40 border-t border-black/10 bg-white/95 p-4 backdrop-blur">
                <div className="mx-auto max-w-3xl">
                    <button
                        data-testid="form-submit"
                        onClick={submit}
                        disabled={saving}
                        className="flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 text-base font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover active:scale-95 disabled:opacity-60"
                    >
                        <Save className="h-5 w-5" />
                        {saving ? "Enregistrement..." : id ? "Enregistrer les modifications" : "Créer le devis"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminDevisForm;
